import { useEffect, useId, useRef, useState } from "react";
import {
  DATE_PRESETS,
  EMPTY_JOB_SEARCH_FILTER,
  JOB_ORIGIN_FILTERS,
  JOB_STATUS_FILTERS,
  activeFilterCount,
  customRangeDayCount,
  datePresetLabel,
  type DatePresetKey,
  type JobOrigin,
  type JobSearchFilter,
} from "@makit/shared";

import { Button, FilterChip } from "../../components/primitives";
import { Calendar } from "./Calendar";

/** 찾기 조건. **조건의 정본은 `packages/shared` 의 `JobSearchFilter` 다** — 앱에서 건
 * 조건이 웹에서 다르게 걸리면 넘나들기가 깨진다. 화면은 칩만 그린다.
 *
 * **열린 상태의 규격 정본은 `W-55b`**(폭 300 · 반경 14 · 달력 포함)이고 `W-20` 도 그것을
 * 그대로 쓴다. 다른 것은 힌트 문구와 상태 칩 하나뿐이다(PRD 7.7.3 · 7.7.6b). */

export const NO_CONDITIONS: JobSearchFilter = EMPTY_JOB_SEARCH_FILTER;

export function conditionCount(filter: JobSearchFilter): number {
  return activeFilterCount(filter);
}

function toggle<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** 걸린 조건 칩의 라벨. 기간은 직접 고른 경우 날짜가 라벨이 된다. */
function periodChipLabel(filter: JobSearchFilter): string {
  if (filter.period !== "CUSTOM") return datePresetLabel(filter.period);
  if (filter.customFrom && filter.customTo) return `${filter.customFrom} – ${filter.customTo}`;
  if (filter.customFrom) return `${filter.customFrom}부터`;
  return datePresetLabel("CUSTOM");
}

export interface FindConditionsProps {
  applied: JobSearchFilter;
  onApply: (next: JobSearchFilter) => void;
  /** `W-20` 은 지금 하는 일을 훑는 자리라 상태 칩에 `만드는 중`이 하나 더 있다. */
  extraStatuses?: ReadonlyArray<{ key: string; label: string }>;
  /** 드롭다운이 열렸는지 바깥이 알아야 패널 아래 자리를 비울 수 있다(`.dropopen`). */
  onOpenChange?: (open: boolean, hasCalendar: boolean) => void;
}

/** **초안(`draft`)은 여는 순간에만 걸린 조건에서 베낀다.**
 *
 * 예전에는 `useEffect(() => { if (open) setDraft(applied); }, [open, applied])` 로 맞췄는데,
 * 그러면 **열려 있는 동안 `applied` 참조가 바뀌기만 해도 셀러가 고르던 것이 되돌아간다.**
 * 두 호출부가 `useState` 값을 넘겨 지금은 드러나지 않지만, `applied` 를 인라인으로 계산해
 * 넘기는 화면이 하나 생기면 리렌더마다 선택이 지워진다. 여는 동작에서 한 번만 베끼면 그
 * 창이 아예 없다. */
export function FindConditions({
  applied,
  onApply,
  extraStatuses = [],
  onOpenChange,
}: FindConditionsProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<JobSearchFilter>(applied);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // 한 화면에 둘이 놓여도 가리키는 짝이 섞이지 않게 id 를 받아 쓴다.
  const panelId = useId();

  const statusOptions = [
    ...JOB_STATUS_FILTERS.map((o) => ({ key: o.key, label: o.label })),
    ...extraStatuses,
  ];

  /** 열림 상태는 **효과가 아니라 동작에서** 알린다. 부모가 인라인 화살표를 넘기면 효과의
   *  의존성이 매 렌더 바뀌어 `setState` → 리렌더 → 효과의 고리가 돈다. */
  function announceOpen(nextOpen: boolean, period: DatePresetKey = draft.period) {
    onOpenChange?.(nextOpen, nextOpen && period === "CUSTOM");
  }

  /** 닫고 열었던 컨트롤로 포커스를 되돌린다. */
  function closeDrop() {
    setOpen(false);
    announceOpen(false);
    buttonRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeDrop();
    }
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        announceOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const count = conditionCount(applied);
  const dayCount =
    draft.customFrom && draft.customTo
      ? customRangeDayCount(draft.customFrom, draft.customTo)
      : 0;

  function setPeriod(period: DatePresetKey) {
    // 달력이 펴지고 접히면 아래에 비워 둘 자리의 크기가 달라진다.
    announceOpen(true, period);
    if (period === "CUSTOM") {
      setDraft({ ...draft, period });
      return;
    }
    // 프리셋으로 돌아가면 고른 날짜를 남기지 않는다. 남기면 다음에 열었을 때
    // 어느 것이 걸린 조건인지 화면과 값이 어긋난다.
    const { customFrom: _from, customTo: _to, ...rest } = draft;
    void _from;
    void _to;
    setDraft({ ...rest, period });
  }

  return (
    <span className="dropwrap" ref={wrapRef}>
      {/* ⌄ 는 이 자리에서 펼친다는 뜻이다 — 웹은 시트 대신 제자리 드롭다운. */}
      <button
        type="button"
        className="btn ghost small"
        aria-expanded={open}
        // 이 자리에서 펼치는 **여닫이**다. `aria-haspopup="true"` 는 메뉴가 열린다는 뜻이라
        // 스크린리더가 없는 메뉴를 기다린다. 여는 것은 조건을 고르는 묶음이다.
        aria-controls={panelId}
        ref={buttonRef}
        onClick={() => {
          const next = !open;
          // 여는 순간에만 걸린 조건을 초안으로 베낀다. 아래에 비울 자리도 그 값으로
          // 알린다 — 초안은 아직 갱신 전이라 `draft.period` 를 보면 한 박자 늦는다.
          if (next) setDraft(applied);
          setOpen(next);
          announceOpen(next, next ? applied.period : draft.period);
        }}
      >
        찾기 조건{count > 0 ? ` ${count}개` : ""}{" "}
        <span className="chev" aria-hidden="true">
          ⌄
        </span>
      </button>

      {open ? (
        <span className="webdrop" id={panelId} role="group" aria-label="찾기 조건">
          <span className="dh">기간 · 하나만</span>
          <span className="fchips">
            {DATE_PRESETS.map((p) => (
              <FilterChip
                key={p.key}
                label={p.label}
                on={draft.period === p.key}
                onClick={() => setPeriod(p.key)}
              />
            ))}
            <FilterChip
              label="직접 고르기"
              on={draft.period === "CUSTOM"}
              onClick={() => setPeriod("CUSTOM")}
            />
          </span>

          {/* 폰처럼 화면을 새로 열지 않는다. 여기가 폰과 갈리는 유일한 자리다. */}
          {draft.period === "CUSTOM" ? (
            <>
              <Calendar
                from={draft.customFrom}
                to={draft.customTo}
                onChange={(next) =>
                  setDraft({
                    ...draft,
                    ...(next.from ? { customFrom: next.from } : { customFrom: undefined }),
                    ...(next.to ? { customTo: next.to } : { customTo: undefined }),
                  })
                }
              />
              <span className="dn">
                고른 기간{" "}
                <span className="mono">
                  {draft.customFrom ?? "시작"} – {draft.customTo ?? "끝"}
                  {dayCount > 0 ? ` · ${dayCount}일` : ""}
                </span>
              </span>
            </>
          ) : null}

          <span className="dh">상태 · 여러 개</span>
          <span className="fchips">
            {statusOptions.map((s) => (
              <FilterChip
                key={s.key}
                label={s.label}
                multi
                on={draft.statusKeys.includes(s.key)}
                onClick={() => setDraft({ ...draft, statusKeys: toggle(draft.statusKeys, s.key) })}
              />
            ))}
          </span>

          <span className="dh">올린 곳 · 여러 개</span>
          <span className="fchips">
            {JOB_ORIGIN_FILTERS.map((o) => (
              <FilterChip
                key={o.key}
                label={o.label}
                multi
                on={draft.origins.includes(o.key)}
                onClick={() => setDraft({ ...draft, origins: toggle(draft.origins, o.key) })}
              />
            ))}
          </span>

          <span className="drow">
            <Button label="초기화" variant="ghost" small onClick={() => setDraft(NO_CONDITIONS)} />
            <Button
              label="적용"
              small
              onClick={() => {
                onApply(draft);
                closeDrop();
              }}
            />
          </span>
        </span>
      ) : null}
    </span>
  );
}

/** 걸린 조건은 목록 화면에서 보여야 한다. 안 보이면 셀러는 작업이 사라진 줄 안다. */
export function AppliedChips({
  applied,
  onChange,
  statusLabel,
}: {
  applied: JobSearchFilter;
  onChange: (next: JobSearchFilter) => void;
  statusLabel: (key: string) => string;
}) {
  const chips: Array<{ key: string; label: string; clear: () => void }> = [];

  if (applied.period !== "ALL") {
    chips.push({
      key: "period",
      label: periodChipLabel(applied),
      clear: () => {
        const { customFrom: _from, customTo: _to, ...rest } = applied;
        void _from;
        void _to;
        onChange({ ...rest, period: "ALL" });
      },
    });
  }
  for (const key of applied.statusKeys) {
    chips.push({
      key: `s-${key}`,
      label: statusLabel(key),
      clear: () => onChange({ ...applied, statusKeys: toggle(applied.statusKeys, key) }),
    });
  }
  for (const origin of applied.origins) {
    chips.push({
      key: `o-${origin}`,
      label: JOB_ORIGIN_FILTERS.find((o) => o.key === origin)?.label ?? origin,
      clear: () => onChange({ ...applied, origins: toggle(applied.origins, origin) }),
    });
  }

  if (chips.length === 0) return <span className="fchips" />;

  return (
    <span className="fchips">
      {chips.map((chip) => (
        <FilterChip key={chip.key} label={chip.label} on removable onClick={chip.clear} />
      ))}
      <FilterChip label="모두 지우기" onClick={() => onChange(NO_CONDITIONS)} />
    </span>
  );
}

/** 상태 칩 키 → 화면 낱말. 화면마다 칩 목록이 달라 함수로 받는다. */
export function makeStatusLabeller(
  extra: ReadonlyArray<{ key: string; label: string }> = [],
): (key: string) => string {
  return (key) =>
    JOB_STATUS_FILTERS.find((o) => o.key === key)?.label ??
    extra.find((o) => o.key === key)?.label ??
    key;
}

export type { JobSearchFilter as Conditions, JobOrigin };
